import type { AssetSheetDownloadFailure, AssetSheetProgress } from '@atlas/types'
import { AssetSheetDerivedRowStatus } from '@atlas/types'
import { Button } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useRetryAssetSheetAsset } from '@/hooks/useAssetSheet'
import { apiErrorMessage } from '@/lib/api-error'
import { formatShortDateTime } from '@/lib/format-short-date-time'
import { notification } from '@/notifications/notification-store'
import { ActivityList } from './ActivityList'
import { AssetSheetView, viewSearch } from './asset-sheet-view'
import { Details } from './Details'
import { ProcessCard, type ProcessCardProps } from './ProcessCard'

type Status = ProcessCardProps['status']

/** The three processes a sync sets off, with their failures, and the recent activity. */
export function OverviewTab({ progress, isStarting }: { progress: AssetSheetProgress; isStarting: boolean }) {
	return (
		<div className="flex flex-col gap-ds-6">
			<SyncCard progress={progress} isStarting={isStarting} />
			<DownloadsCard progress={progress} />
			<TaggingCard progress={progress} />
			<ActivityList />
		</div>
	)
}

function syncStatus({ sync }: AssetSheetProgress, isStarting: boolean): Status {
	if (sync.running) return { label: 'Running', variant: 'warning' }
	if (isStarting) return { label: 'Starting', variant: 'warning' }
	return sync.lastSyncedAt ? { label: 'Idle', variant: 'muted' } : { label: 'Never run', variant: 'muted' }
}

function SyncCard({ progress, isStarting }: { progress: AssetSheetProgress; isStarting: boolean }) {
	const { rows, lastSyncedAt } = progress.sync
	return (
		<ProcessCard
			step={1}
			title="Sync"
			description="Reads the workbook's ready rows and matches each cell to the names ATLAS has for the row's country. A row with a cell that matches nothing is blocked and keeps what it had."
			status={syncStatus(progress, isStarting)}
			stats={[
				{ label: 'Last synced', value: lastSyncedAt ? formatShortDateTime(lastSyncedAt) : 'Never' },
				{ label: 'Mapped rows', value: rows.MAPPED },
				{ label: 'Blocked rows', value: rows.BLOCKED },
				{ label: 'Removed rows', value: rows.REMOVED },
			]}
		>
			{(rows.BLOCKED > 0 || rows.REMOVED > 0) && (
				<p className="flex flex-wrap gap-ds-4 text-ds-sm">
					{rows.BLOCKED > 0 && (
						<RowsLink status={AssetSheetDerivedRowStatus.BLOCKED}>
							See the {rows.BLOCKED} blocked {rows.BLOCKED === 1 ? 'row' : 'rows'}
						</RowsLink>
					)}
					{rows.REMOVED > 0 && (
						<RowsLink status={AssetSheetDerivedRowStatus.REMOVED}>
							See the {rows.REMOVED} removed {rows.REMOVED === 1 ? 'row' : 'rows'}
						</RowsLink>
					)}
				</p>
			)}
		</ProcessCard>
	)
}

/** A link to the Rows tab filtered to one status. */
function RowsLink({ status, children }: { status: AssetSheetDerivedRowStatus; children: ReactNode }) {
	return (
		<Link
			to={{ search: viewSearch(AssetSheetView.ROWS, { status }) }}
			className="text-ds-text underline underline-offset-2 hover:text-ds-text-muted"
		>
			{children}
		</Link>
	)
}

function downloadsStatus({ downloads }: AssetSheetProgress): Status {
	if (downloads.assets === 0) return { label: 'Waiting for a sync', variant: 'muted' }
	if (downloads.inProgress + downloads.notStarted > 0) return { label: 'In progress', variant: 'warning' }
	if (downloads.failed > 0) return { label: `${downloads.failed} failed`, variant: 'danger' }
	return { label: 'Done', variant: 'success' }
}

function DownloadsCard({ progress }: { progress: AssetSheetProgress }) {
	const { downloads } = progress
	const retry = useRetryAssetSheetAsset()
	const filesDone = downloads.files.success
	const filesTotal = Object.values(downloads.files).reduce((a, b) => a + b, 0)

	const onRetry = (assetCode: string) =>
		retry.mutate(assetCode, {
			onSuccess: () => notification.success(`${assetCode} is downloading again.`),
			onError: (error) => notification.error(apiErrorMessage(error, `${assetCode} could not be retried.`)),
		})

	return (
		<ProcessCard
			step={2}
			title="Downloads"
			description="Each asset code a mapped row references is imported from Veeva Vault: the source file and its renditions."
			status={downloadsStatus(progress)}
			progress={{ done: downloads.completed, total: downloads.assets, noun: 'assets downloaded' }}
			stats={[
				{ label: 'Not started', value: downloads.notStarted },
				{ label: 'In progress', value: downloads.inProgress },
				{ label: 'Failed', value: downloads.failed },
				{ label: 'Files downloaded', value: `${filesDone} of ${filesTotal}` },
			]}
		>
			{downloads.failures.length > 0 && (
				<p className="text-ds-sm">
					<RowsLink status={AssetSheetDerivedRowStatus.HAS_FAILURES}>See the rows with failures</RowsLink>
				</p>
			)}
			{downloads.failures.length > 0 && (
				<Details summary={`Failures (${downloads.failures.length})`} isOpen>
					{downloads.failures.map((f) => (
						<li key={f.assetCode} className="flex items-start justify-between gap-ds-3 text-ds-sm">
							<FailureText failure={f} />
							{f.canRetry ? (
								<Button variant="secondary" size="sm" onPress={() => onRetry(f.assetCode)}>
									Retry
								</Button>
							) : (
								<span className="shrink-0 text-ds-xs text-ds-text-muted">Fix the sheet or Vault, then sync</span>
							)}
						</li>
					))}
				</Details>
			)}
		</ProcessCard>
	)
}

function taggingStatus({ tagging }: AssetSheetProgress): Status {
	const { files, tagged, stalled, toRemove } = tagging
	if (files === 0 && toRemove === 0) return { label: 'Waiting for downloads', variant: 'muted' }
	if (tagged + stalled < files || toRemove > 0) return { label: 'In progress', variant: 'warning' }
	if (stalled > 0) return { label: `${stalled} not tagged`, variant: 'danger' }
	return { label: 'Done', variant: 'success' }
}

function TaggingCard({ progress }: { progress: AssetSheetProgress }) {
	const { tagging } = progress
	return (
		<ProcessCard
			step={3}
			title="Tagging"
			description="Each downloaded file gets the brand, product, country, species and file type of every row referencing it. Tags no row produces any more are removed."
			status={taggingStatus(progress)}
			progress={{ done: tagging.tagged, total: tagging.files, noun: 'files tagged' }}
			stats={[
				{ label: 'Files tagged', value: tagging.tagged },
				{ label: 'Waiting', value: tagging.files - tagging.tagged - tagging.stalled },
				{ label: 'Stalled', value: tagging.stalled },
				{ label: 'Tags to remove', value: tagging.toRemove },
			]}
		>
			{tagging.stalled > 0 && (
				<p className="text-ds-sm text-ds-text-muted">
					{tagging.stalled} {tagging.stalled === 1 ? 'file is' : 'files are'} still untagged 15 minutes after
					downloading and the last sync. Sync now sends them to tagging again.
				</p>
			)}
		</ProcessCard>
	)
}

/** A reason that already names the asset code stands alone, its API field name read as words; any other goes under the code. */
function FailureText({ failure: { assetCode, reason, rowKeys } }: { failure: AssetSheetDownloadFailure }) {
	const named = reason.includes(assetCode)
	const rows = rowKeys.join(', ')
	return (
		<div className="flex min-w-0 flex-col gap-ds-1 wrap-anywhere">
			<span className={named ? 'text-ds-text' : 'font-ds-mono text-ds-text'}>
				{named ? reason.replace(/^assetCode /, 'Asset code ') : assetCode}
			</span>
			<span className="text-ds-text-muted">{named ? `Rows ${rows}` : `${reason} · rows ${rows}`}</span>
		</div>
	)
}
